import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class QuantumErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('🚨 Quantum Error Boundary caught:', error, errorInfo);
    this.setState({
      error,
      errorInfo
    });
    
    // Log to monitoring service (if configured)
    // logErrorToService(error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
    
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  handleGoBack = (): void => {
    window.history.back();
    setTimeout(() => this.handleReset(), 100);
  };

  handleGoHome = (): void => {
    window.location.href = '/commander';
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl w-full bg-slate-800/90 backdrop-blur-xl border border-red-500/30 rounded-3xl p-8 shadow-2xl"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="p-4 rounded-2xl bg-red-500/20 border border-red-500/30">
                <AlertTriangle className="h-8 w-8 text-red-400" />
              </div>
              <div>
                <h2 className="text-2xl font-black text-white">Quantum System Error</h2>
                <p className="text-white/70">The quantum core encountered an unexpected state</p>
              </div>
            </div>

            <div className="bg-black/30 rounded-xl p-4 mb-6 border border-white/10">
              <p className="text-red-400 font-mono text-sm break-all">
                {this.state.error?.toString() || 'Unknown quantum fluctuation'}
              </p>
              {this.state.errorInfo && (
                <details className="mt-4">
                  <summary className="text-white/60 text-sm cursor-pointer hover:text-white/80">
                    View quantum stack trace
                  </summary>
                  <pre className="mt-2 text-xs text-white/40 font-mono overflow-auto max-h-60 p-2 bg-black/50 rounded">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>

            <div className="flex flex-wrap gap-4">
              <button
                onClick={this.handleReset}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-bold hover:from-blue-700 hover:to-purple-700 transition-all flex items-center gap-2 justify-center"
              >
                <RefreshCw className="h-4 w-4" />
                Reset Quantum State
              </button>
              <button
                onClick={this.handleGoBack}
                className="flex-1 px-6 py-3 bg-white/10 text-white rounded-xl font-bold hover:bg-white/20 transition-all flex items-center gap-2 justify-center"
              >
                <ArrowLeft className="h-4 w-4" />
                Go Back
              </button>
              <button
                onClick={this.handleGoHome}
                className="flex-1 px-6 py-3 bg-white/10 text-white rounded-xl font-bold hover:bg-white/20 transition-all flex items-center gap-2 justify-center"
              >
                <Home className="h-4 w-4" />
                Quantum Commander
              </button>
            </div>

            <p className="text-white/40 text-xs text-center mt-6">
              Error ID: {Date.now().toString(36)} • Report this to quantum support
            </p>
          </motion.div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default QuantumErrorBoundary;